import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Store, ArrowRight, MapPin } from "lucide-react";
import { GlassCard, SectionTitle } from "../components/common/ui";
import StoreBranding from "../components/common/StoreBranding";
import AgeBadge from "../components/common/AgeBadge";
import api from "../lib/api";

export default function Stores() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Our Stores · Clacton Fireworks";
    api.get("/stores").then(({ data }) => setStores(data || [])).catch(() => {}).finally(() => setLoading(false));
    return () => { document.title = "Clacton Fireworks"; };
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20" data-testid="stores-directory-page">
      <SectionTitle
        eyebrow="Collection points"
        title="Our Stores"
        subtitle="Reserve premium fireworks online and collect in store. Find your nearest Clacton Fireworks collection point below."
      />

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : stores.length === 0 ? (
        <GlassCard className="mt-10 p-12 text-center text-muted-foreground" hover={false}>
          <Store className="h-8 w-8 mx-auto text-gold/60" />
          <p className="mt-3">Store details are being finalised — please check back soon.</p>
        </GlassCard>
      ) : (
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="stores-grid">
          {stores.map((s) => (
            <Link
              key={s.store_id}
              to={`/stores/${s.slug}`}
              data-testid={`store-card-${s.slug}`}
              className="group block cf-reveal"
            >
              <GlassCard className="overflow-hidden h-full flex flex-col">
                <div className={`relative h-44 overflow-hidden ${s.primary_logo ? "bg-white" : "bg-black/40"}`}>
                  {s.primary_logo ? (
                    <img src={s.primary_logo} alt={`${s.name} logo`}
                      className="absolute inset-0 h-full w-full object-contain p-8 transition-transform duration-500 group-hover:scale-[1.05]" />
                  ) : s.featured_image ? (
                    <img src={s.featured_image} alt={s.name}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center"><Store className="h-9 w-9 text-gold/40" /></div>
                  )}
                  {s.is_collection_point && (
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-black/60 text-gold border border-gold/30 rounded-full px-2.5 py-1">
                      Collection Point
                    </span>
                  )}
                </div>
                <div className="p-6 flex flex-col grow">
                  <h3 className="font-playfair text-xl text-white group-hover:text-gold transition-colors">{s.name}</h3>
                  <StoreBranding store={s} showName={false} className="mt-3" />
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
                    View store <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </GlassCard>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="stores-age-notice">
        <AgeBadge size={40} linked />
        <span className="text-sm text-white/50">Please bring valid photo ID when you collect.</span>
      </div>

      <div className="mt-6">
        <Link to="/areas-we-serve" data-testid="stores-areas-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:opacity-90">
          <MapPin className="h-4 w-4" /> See all the areas we serve
        </Link>
      </div>
    </div>
  );
}
